import React, { useEffect, useState } from 'react'
import { useLocation, useNavigate } from "react-router-dom";
import { useAcceptOrRejectBlog, useGetBlogsById, useUpdateBlogStatus } from "../../queries/blog";
import Loader from "../../components/Common/Loader";
import Chip from "../../components/Chip";
import { ActionButton } from "../../components/Forms/ActionButton";
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import EditIcon from '../../utils/media/icons/edit_icon.png';
import RichTextEditor from "../../components/Forms/RichTextEditor";
import Modal from "../Submissions/components/Modal";

const BlogDetails = () => {
    const { state } = useLocation();
    const blogId = state?.blogId;
    const navigate = useNavigate();

    const { data: blog, isPending, isError } = useGetBlogsById(blogId);
    // const { mutateAsync: updateBlogStatus, isLoading: isMutating } = useUpdateBlogStatus();
    const [description, setDescription] = useState("");
    const [rejectionModelOpen, setRejectionModelOpen] = useState(false);
    const [rejectionReason, setRejectionReason] = useState("");

    const { mutateAsync: updateBlogStatus, isPending: isMutating } = useAcceptOrRejectBlog();
    
    useEffect(() => {
        if (blog?.description) setDescription(blog.description);
    }, [blog]);

    const renderChipStatus = (status) => {
        if (status === 'active') return <Chip value={status} variant={'filled'} color="green" />;
        if (status === 'rejected') return <Chip value={status} variant={'filled'} color="red" />;
        if (status === 'inactive') return <Chip value={status} variant={'filled'} color="red" />;
        if (status === 'auto approved') return <Chip value={status} variant={'filled'} color="purple" />;
        return <Chip value={status} variant={'filled'} color="blue" />;
    };

    // Accept blog (set status to 'active')
    const handleAccept = async (blogId) => {
        await updateBlogStatus({ status: 'approved', blogId }, {
            onSuccess: () => {
                toast.success("Blog approved!");
                setTimeout(() => {
                    navigate("/blogs/blog-list-user");
                }, 2000);
            },
            onError: () => {
                toast.error("Failed to accept blog.");
            }
        });
    };

    // Reject blog (set status to 'rejected' with reason)
    const handleReject = async (blogId) => {
        await updateBlogStatus({ status: 'rejected', blogId, rejectReason: rejectionReason }, {
            onSuccess: () => {
                toast.success("Blog rejected successfully!");
                setTimeout(() => {
                    navigate("/blogs/blog-list-user");
                }, 2000);
            },
            onError: () => {
                toast.error("Failed to reject blog.");
            }
        });
    };

    // Reset handler (reloads details)
    const handleReset = () => {
        window.location.reload();
    };

    // Cancel handler (go back to list)
    const handleCancel = () => {
        navigate("/blogs/blog-list-user");
    };

    const renderBlogCreator = (item) => {
        if(item?.createdByUser){
            return item?.createdByUser?.username || "Unknown User"
        }
        else if(item?.blogType === "System Generated"){
            return "Admin"
        }
        else if(item?.createdByNgo){
            return item?.createdByNgo?.ngoName || "Unknown NGO"
        }
        else if(item?.createdByCompany){
            return item?.createdByCompany?.companyName || "Unknown Company"
        }
    }
    
    if (isPending) return <Loader />;
    if (isError || !blog) return <div className="text-center mt-10">Error loading blog details.</div>;

    return (
        <div className="flex flex-col text-base font-semibold text-gray-900 rounded ">
            <div className="bg-white rounded shadow-[0px_0px_25px_rgba(94,92,154,0.1)] p-5">
                <div className="flex justify-between items-center">
                    <h1 className="gap-2.5 w-fit text-xl font-bold max-md:max-w-full text-primary capitalize cursor-pointer">
                        Blog Details
                    </h1>
                    <div className="flex items-center justify-between gap-3">
                        {renderChipStatus(blog.status)}
                        <button
                            className="rounded p-1.5 bg-[rgba(0,122,155,0.1)] hover:bg-[rgba(0,122,155,0.2)] min-w-5 relative group"
                            onClick={() => navigate('/blogs/create-blog', { state: { blogId: blog?._id } })}
                        >
                            <img src={EditIcon} alt="edit" className="w-4" />
                            <span className="absolute top-full left-1/2 transform -translate-x-1/2 mb-1 hidden group-hover:block bg-[rgba(0,122,155,0.5)] text-white text-xs rounded py-1 px-2">
                                Edit Blog
                            </span>
                        </button>
                    </div>
                </div>
                <div className="flex flex-col gap-5 mt-5">
                    <div className="flex flex-col md:flex-row gap-8 justify-between">
                        <div className="flex flex-col gap-5 flex-1">
                            <div className="flex flex-row gap-2">
                                <h2 className="text-lg font-semibold text-primary">Title: </h2>
                                <p>{blog?.title}</p>
                            </div>
                            <div className="flex flex-row gap-2">
                                <h2 className="text-lg font-semibold text-primary">Category: </h2>
                                <p>{blog?.category?.name || '-'}</p>
                            </div>
                            <div className="flex flex-row gap-2">
                                <h2 className="text-lg font-semibold text-primary">Blog Type: </h2>
                                <p>{blog?.blogType}</p>
                            </div>
                            <div className="flex flex-row gap-2">
                                <h2 className="text-lg font-semibold text-primary">Blog Creator: </h2>
                                {/* <p>{blog?.createdByUser?.username || blog?.createdByAdmin ? 'Admin' : blog?.createdByNgo?.ngoName || blog?.createdByCompany?.companyName || '-'}</p> */}
                                <p>{renderBlogCreator(blog)}</p>
                            </div>
                            <div className="flex flex-row gap-2">
                                <h2 className="text-lg font-semibold text-primary">Status: </h2>
                                <p>{blog?.status}</p>
                            </div>
                            <div className="flex flex-row gap-2">
                                <h2 className="text-lg font-semibold text-primary">Start Date: </h2>
                                <p>{blog?.startDate ? new Date(blog.startDate).toLocaleString('en-US', { day: '2-digit', month: 'long', year: 'numeric' }) : '-'}</p>
                            </div>
                            <div className="flex flex-row gap-2">
                                <h2 className="text-lg font-semibold text-primary">End Date: </h2>
                                <p>{blog?.endDate ? new Date(blog.endDate).toLocaleString('en-US', { day: '2-digit', month: 'long', year: 'numeric' }) : '-'}</p>
                            </div>
                            <div className="flex flex-row gap-2">
                                <h2 className="text-lg font-semibold text-primary">Connected Challenge: </h2>
                                <p>{blog?.connectedChallenge?.name || '-'}</p>
                            </div>
                        </div>
                        {blog?.coverImage && blog.coverImage[0] && (
                            <img src={blog.coverImage[0]} alt="cover" className="w-80 h-60 object-cover rounded" />
                        )}
                    </div>
                    <div className="flex flex-col gap-2 mt-4">
                        <h2 className="text-lg font-semibold text-primary">Description:</h2>
                        <div className="prose max-w-none">
                            <RichTextEditor value={description} onChange={() => {}} readOnly={true} />
                        </div>
                    </div>
                    {blog?.featuredImages && blog.featuredImages.length > 0 && (
                        <div className="flex flex-col gap-2 mt-4">
                            <h2 className="text-lg font-semibold text-primary">Featured Images:</h2>
                            <div className="flex flex-wrap gap-3">
                                {blog.featuredImages.map((img, idx) => (
                                    <img key={idx} src={img} alt={`featured-${idx}`} className="w-32 h-24 object-cover rounded" />
                                ))}
                            </div>
                        </div>
                    )}
                    {blog?.imageGallery && blog.imageGallery.length > 0 && (
                        <div className="flex flex-col gap-2 mt-4">
                            <h2 className="text-lg font-semibold text-primary">Image Gallery:</h2>
                            <div className="flex flex-wrap gap-3">
                                {blog.imageGallery.map((img, idx) => (
                                    <img key={idx} src={img} alt={`gallery-${idx}`} className="w-32 h-24 object-cover rounded" />
                                ))}
                            </div>
                        </div>
                    )}
                    <div className="flex flex-row justify-between gap-5 mt-5">
                        <div className="flex flex-row gap-3">
                            <ActionButton
                                variant={"cancel"}
                                text={"Cancel"}
                                handleSubmit={handleCancel}
                            />
                        </div>
                        {
                            blog?.status !== 'active' && (
                                <div className="flex flex-row gap-3">
                                    <ActionButton
                                        variant={"cancel"}
                                        text={"Reject"}
                                        handleSubmit={() => setRejectionModelOpen(true)}
                                        disabled={isMutating}
                                    />
                                    <ActionButton
                                        variant={"submit"}
                                        text={"Approve"}
                                        handleSubmit={() => handleAccept(blog?._id)}
                                        disabled={isMutating}
                                    />
                                </div>
                            )
                        }
                    </div>
                    {/* Rejection Modal */}
                    <Modal isOpen={rejectionModelOpen} onClose={() => setRejectionModelOpen(false)} className="max-h-96 overflow-y-auto">
                        <div className="flex flex-col gap-5">
                            <h1 className="text-lg font-semibold text-primary">Are you sure you want to reject this blog?</h1>
                            <RichTextEditor
                                label={'Reason for rejection'}
                                placeholder={'Please provide a reason for rejection'}
                                value={rejectionReason}
                                onChange={setRejectionReason}
                                required={true}
                            />
                            <div className="flex flex-row justify-end gap-5">
                                <ActionButton
                                    variant={"cancel"}
                                    text={"Cancel"}
                                    handleSubmit={() => setRejectionModelOpen(false)}
                                />
                                <ActionButton
                                    variant={"submit"}
                                    text={"Reject"}
                                    disabled={isMutating}
                                    handleSubmit={() => {
                                        handleReject(blog?._id);
                                        setRejectionModelOpen(false);
                                    }}
                                />
                            </div>
                        </div>
                    </Modal>
                </div>
            </div>
            <ToastContainer position="top-right" />
        </div>
    );
}

export default BlogDetails;